import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { cbomApi, sourcesApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { CryptoAssetsTable, FindingsSummaryBar } from '@/components/common/CryptoAssetsTable'
import type { FindingEntry, FindingsSummary, AssetType, Severity, Modality, QuantumRisk, AssetLifecycle } from '@/api/types'

const PAGE_SIZE = 50

const ASSET_TYPES: AssetType[] = ['algorithm', 'certificate', 'key', 'protocol', 'kdf']
const SEVERITY_LEVELS: Severity[] = ['critical', 'high', 'medium', 'low', 'info']
const MODALITIES: Modality[] = ['source', 'binary', 'runtime', 'network', 'infra', 'manual']
const QUANTUM_RISKS: QuantumRisk[] = ['vulnerable', 'partial', 'hybrid', 'safe', 'unknown']

interface AssetFilter {
  search: string
  assetType: AssetType | '' | 'others'
  severity: Severity | ''
  quantumSafe: 'all' | 'safe' | 'unsafe'
  quantumRisk: QuantumRisk | ''
  modality: Modality | ''
  sourceId: string
  primitive: string
  usageContext: string
  lifecycle: AssetLifecycle | ''
}

const DEFAULT_FILTER: AssetFilter = {
  search: '',
  assetType: '',
  severity: '',
  quantumSafe: 'all',
  quantumRisk: '',
  modality: '',
  sourceId: '',
  primitive: '',
  usageContext: '',
  lifecycle: '',
}

const PRIMITIVES = ['ae', 'dh', 'dsa', 'ec', 'ekdf', 'hash', 'kdf', 'mac', 'rsa', 'signature', 'xof']
const USAGE_CONTEXTS = ['transport', 'storage', 'signing', 'auth']

export default function Assets() {
  const { currentOrgId } = useOrgStore()
  const [filter, setFilter] = useState<AssetFilter>(DEFAULT_FILTER)
  const [downloading, setDownloading] = useState<'cbom' | 'sarif' | null>(null)

  const handleDownload = async (type: 'cbom' | 'sarif') => {
    setDownloading(type)
    try {
      if (type === 'cbom') await cbomApi.downloadOrgCbom(currentOrgId)
      else await cbomApi.downloadOrgSarif(currentOrgId)
    } finally {
      setDownloading(null)
    }
  }
  const [pages, setPages] = useState<FindingEntry[][]>([])
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState<FindingsSummary | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isFetchingRef = useRef(false)

  // Reset pagination when filter changes. Keep total/summary stale so the
  // filter bar and summary bar stay visible while the new page loads.
  // Do NOT clear pages here — the data effect below uses offset===0 as a
  // "start fresh" signal, which avoids an intermediate empty-table flash.
  useEffect(() => {
    setOffset(0)
    setHasMore(false)
  }, [filter.search, filter.assetType, filter.severity, filter.quantumSafe, filter.quantumRisk, filter.modality, filter.sourceId, filter.primitive, filter.usageContext])

  const { isLoading, isFetching, isError, error, refetch, data: findingsData } = useQuery({
    queryKey: ['org-findings', currentOrgId, filter, offset],
    // Pure fetch — no side effects. State is synced via the effect below so
    // that cached results (isFetching=false) still populate pages correctly.
    queryFn: () => cbomApi.fetchOrgFindings(currentOrgId, {
      limit: PAGE_SIZE,
      offset,
      name: filter.search || undefined,
      asset_type: (filter.assetType === 'others' ? undefined : filter.assetType) || undefined,
      modality: filter.modality || undefined,
      severity: filter.severity || undefined,
      source_id: filter.sourceId || undefined,
      is_quantum_safe:
        filter.quantumSafe === 'all' ? undefined : filter.quantumSafe === 'safe',
      quantum_risk: filter.quantumRisk || undefined,
      primitive: filter.primitive || undefined,
      usage_context: filter.usageContext || undefined,
      lifecycle: filter.lifecycle || undefined,
    }),
  })

  // Sync TanStack Query data → local pages accumulator.
  // offset===0 means "new filter or first page" → replace pages entirely so
  // stale pages from the previous filter don't bleed through.
  useEffect(() => {
    if (!findingsData) return
    const batch = findingsData.findings
    setPages((prev) => {
      if (offset === 0) return [batch]
      const next = [...prev]
      next[offset / PAGE_SIZE] = batch
      return next
    })
    setHasMore(offset + batch.length < findingsData.total)
    setTotal(findingsData.total)
    if (offset === 0) setSummary(findingsData.summary)
  }, [findingsData, offset])

  const { data: sourcesResp } = useQuery({
    queryKey: ['sources', currentOrgId],
    queryFn: () => sourcesApi.list(currentOrgId),
  })

  const sources = useMemo(() => sourcesResp?.data ?? [], [sourcesResp?.data])
  const sourceMap = useMemo(
    () => Object.fromEntries(sources.map((s) => [s.id, s.name])),
    [sources]
  )
  const allFindings = useMemo(() => {
    const seen = new Set<string>()
    return pages.flat().filter(f => {
      if (seen.has(f.id)) return false
      seen.add(f.id)
      return true
    })
  }, [pages])

  useEffect(() => {
    if (filter.assetType !== 'others') return
    const hasOthers = allFindings.some(f => !ASSET_TYPES.includes(f.asset_type as AssetType))
    if (!hasOthers && hasMore && !isFetching) {
      setOffset(o => o + PAGE_SIZE)
    }
  }, [filter.assetType, allFindings, hasMore, isFetching])

  // Keep ref in sync so the observer callback can read latest isFetching
  // without being a dep of the effect (which would recreate the observer on
  // every fetch state change and cause cascade loads).
  isFetchingRef.current = isFetching

  const loadMore = useCallback(() => setOffset((p) => p + PAGE_SIZE), [])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingRef.current) loadMore()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasMore, loadMore])

  const hasActiveFilters =
    filter.search || filter.assetType || filter.severity ||
    filter.quantumSafe !== 'all' || filter.quantumRisk || filter.modality || filter.sourceId ||
    filter.primitive || filter.usageContext

  return (
    <div className="space-y-6" id="assets-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">
            Crypto Assets
          </h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            All cryptographic assets discovered across your organisation
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['cbom', 'sarif'] as const).map((type) => (
            <button
              key={type}
              onClick={() => handleDownload(type)}
              disabled={downloading !== null}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] text-text-light dark:text-text-dark rounded-lg hover:border-primary hover:text-primary transition-colors disabled:opacity-50"
            >
              {downloading === type ? (
                <span className="material-icons text-base animate-spin">autorenew</span>
              ) : (
                <span className="material-icons text-base">download</span>
              )}
              {type === 'cbom' ? 'CycloneDX' : type.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Error state */}
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-error">
              Failed to load findings
            </p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
              This may mean no scans have been completed yet for org{' '}
              <code className="font-mono">{currentOrgId}</code>.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="text-sm text-error hover:underline shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty state — no assets yet */}
      {!isLoading && !isError && total === 0 && offset === 0 && (
        <div className="flex items-start gap-3 p-4 bg-secondary/10 dark:bg-secondary/15 border border-secondary/25 dark:border-secondary/30 rounded-xl">
          <span className="material-icons text-primary dark:text-secondary text-xl mt-0.5">info_outline</span>
          <div>
            <p className="text-sm font-medium text-text-light dark:text-text-dark">
              No crypto assets discovered yet
            </p>
            <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
              Run a scan on one of your{' '}
              <Link to="/sources" className="text-primary dark:text-secondary underline">
                discovery sources
              </Link>{' '}
              to populate this view.
            </p>
          </div>
        </div>
      )}

      {/* Summary bar — stable across pages, only updates on filter change (offset === 0) */}
      {summary && summary.total > 0 && (
        <FindingsSummaryBar
          total={summary.total}
          quantumSafe={summary.quantum_safe}
          quantumUnsafe={summary.quantum_unsafe}
          byRisk={summary.by_risk}
          byLifecycle={summary.by_lifecycle}
        />
      )}

      {/* Filters */}
      {(total > 0 || hasActiveFilters) && (
        <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
          <div className="flex flex-wrap gap-3">
            <input
              type="text"
              placeholder="Search by name…"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark w-52 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              value={filter.assetType}
              onChange={(e) => setFilter({ ...filter, assetType: e.target.value as AssetType | '' | 'others' })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Types</option>
              {ASSET_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
              <option value="others">others</option>
            </select>
            <select
              value={filter.severity}
              onChange={(e) => setFilter({ ...filter, severity: e.target.value as Severity | '' })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Severities</option>
              {SEVERITY_LEVELS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={filter.quantumSafe}
              onChange={(e) =>
                setFilter({ ...filter, quantumSafe: e.target.value as 'all' | 'safe' | 'unsafe' })
              }
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="all">All Quantum Status</option>
              <option value="safe">Quantum Safe</option>
              <option value="unsafe">At Risk</option>
            </select>
            {/* A live monitor keeps reporting an asset after it stops seeing
                it, marked stale, so "what is live right now" needs a filter.
                Assets from one-shot scans carry no lifecycle and count as
                active. */}
            <select
              value={filter.lifecycle}
              onChange={(e) => setFilter({ ...filter, lifecycle: e.target.value as AssetLifecycle | '' })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Lifecycle</option>
              <option value="active">Active</option>
              <option value="stale">Stale</option>
            </select>
            <select
              value={filter.quantumRisk}
              onChange={(e) => setFilter({ ...filter, quantumRisk: e.target.value as QuantumRisk | '' })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Quantum Risk</option>
              {QUANTUM_RISKS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={filter.modality}
              onChange={(e) => setFilter({ ...filter, modality: e.target.value as Modality | '' })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Modalities</option>
              {MODALITIES.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <select
              value={filter.primitive}
              onChange={(e) => setFilter({ ...filter, primitive: e.target.value })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Primitives</option>
              {PRIMITIVES.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <select
              value={filter.usageContext}
              onChange={(e) => setFilter({ ...filter, usageContext: e.target.value })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Usage Contexts</option>
              {USAGE_CONTEXTS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select
              value={filter.sourceId}
              onChange={(e) => setFilter({ ...filter, sourceId: e.target.value })}
              className="pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              <option value="">All Sources</option>
              {sources.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            {hasActiveFilters && (
              <button
                onClick={() => setFilter(DEFAULT_FILTER)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark"
              >
                <span className="material-icons text-base">clear</span>
                Clear
              </button>
            )}
          </div>
          {total > 0 && (
            <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
              Showing {allFindings.length} of {total} assets
            </p>
          )}
        </div>
      )}

      {/* Assets table */}
      <CryptoAssetsTable
        findings={filter.assetType === 'others' ? allFindings.filter(f => !ASSET_TYPES.includes(f.asset_type as AssetType)) : allFindings}
        showScanColumn
        showSourceColumn
        sourceMap={sourceMap}
        isLoading={pages.length === 0 && (isLoading || isFetching)}
        emptyMessage={
          total === 0
            ? 'Run a scan to discover crypto assets.'
            : 'No assets match the current filters.'
        }
      />

      {/* Infinite scroll sentinel + spinner */}
      <div ref={sentinelRef} className="h-1" />
      {isFetching && !isLoading && (
        <div className="flex justify-center py-4">
          <span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark">
            autorenew
          </span>
        </div>
      )}
    </div>
  )
}
